import { useReducedMotion } from "framer-motion";
import { memo, useEffect, useState, type ReactNode } from "react";
import { SummaryChips } from "@funnel-ui/primitives/SummaryChips";

type ValidationScreenProps = {
  emoji?: string;
  illustration?: ReactNode;
  headline: string;
  message?: string;
  /**
   * Optional summary chips rendered between the message and the auto-advance
   * progress bar. Useful for echoing the user's funnel answers — e.g.
   * `["Warm", "Movie night", "Detailed & thoughtful"]`.
   */
  chips?: string[];
  /** Auto-advance after this many ms. Set to 0 to disable. */
  autoAdvanceMs?: number;
  onComplete?: () => void;
  className?: string;
  testId?: string;
};

const ValidationScreenImpl = ({
  emoji = "✨",
  illustration,
  headline,
  message,
  chips,
  autoAdvanceMs = 1800,
  onComplete,
  className,
  testId,
}: ValidationScreenProps) => {
  const prefersReduced = useReducedMotion();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (autoAdvanceMs <= 0 || !onComplete) return;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number): void => {
      const ratio = Math.min(1, (now - start) / autoAdvanceMs);
      setProgress(ratio);
      if (ratio >= 1) {
        onComplete();
      } else {
        raf = requestAnimationFrame(tick);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [autoAdvanceMs, onComplete]);

  return (
    <section
      data-testid={testId}
      className={`flex flex-col items-center justify-center gap-6 py-12 ${className ?? ""}`}
    >
      <div
        className="text-7xl"
        style={{
          animation: prefersReduced
            ? undefined
            : "fc-fade-in-up 360ms cubic-bezier(0.32,0.72,0,1) backwards, fc-pop 320ms cubic-bezier(0.32,0.72,0,1) backwards",
        }}
      >
        {illustration ?? emoji}
      </div>
      <h1
        className="max-w-md text-center text-2xl font-semibold leading-tight"
        style={{ color: "hsl(var(--brand-bg-foreground))" }}
      >
        {headline}
      </h1>
      {message && (
        <p className="max-w-md text-center text-sm" style={{ color: "hsl(var(--hero-fg-muted))" }}>
          {message}
        </p>
      )}
      {chips && chips.length > 0 && (
        <SummaryChips
          chips={chips}
          prefersReduced={prefersReduced ?? false}
          ariaLabel="Summary"
          staggerMs={80}
        />
      )}
      {autoAdvanceMs > 0 && onComplete && (
        <div
          className="mt-2 h-1 w-32 overflow-hidden rounded-full"
          style={{ background: "hsl(var(--brand-bg-foreground) / 0.10)" }}
        >
          <div
            className="h-full rounded-full"
            style={{
              width: `${progress * 100}%`,
              background: "hsl(var(--brand-primary))",
              transition: prefersReduced ? "none" : "width 60ms linear",
            }}
          />
        </div>
      )}
    </section>
  );
};

export const ValidationScreen = memo(ValidationScreenImpl);

export default ValidationScreen;
